import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import { cn } from '@/lib/utils'
import { BlurFade } from '@/components/ui/blur-fade'
import ImageLoading from '@/components/ui/image-loading'
import { Button } from '@/components/ui/button'
import { IconDownload, IconPlayerPlay, IconPlayerPause } from '@tabler/icons-react'

interface AnimatedMarkdownMusicProps {
  src: string
  alt: string
  isStreaming: boolean
}

export const AnimatedMarkdownMusic: React.FC<AnimatedMarkdownMusicProps> = ({
  src,
  alt,
  isStreaming
}) => {
  const [showMusic, setShowMusic] = useState(false)
  const [musicLoaded, setMusicLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [processedSrc, setProcessedSrc] = useState<string>(src)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState<number>(0)
  const [currentTime, setCurrentTime] = useState<number>(0)

  // Check if this is a trusted losi.one URL (now has HTTPS, no proxy needed)
  const isLosiUrl = src.includes('auth.losi.one') || src.includes('mapi.losi.one')

  useEffect(() => {
    if (!isStreaming && src) {
      const timer = setTimeout(() => setShowMusic(true), 300)
      return () => clearTimeout(timer)
    }
  }, [isStreaming, src])

  useEffect(() => {
    setProcessedSrc(src)
  }, [src])

  const handleDownload = async () => {
    if (!processedSrc) return
    setIsDownloading(true)
    try {
      // For losi.one URLs, download directly since they now have HTTPS
      if (isLosiUrl) {
        const link = document.createElement('a')
        link.href = processedSrc
        link.download = `losi_music_${Date.now()}.mp3`
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        // For other URLs, use the proxy for CORS handling
        const response = await fetch(`/api/music/download?url=${encodeURIComponent(processedSrc)}`)
        if (!response.ok) throw new Error('Network response was not ok')
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `losi_music_${Date.now()}.mp3`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      }
    } catch (error) {
      console.error('Download failed:', error)
      setLoadError('Failed to download music')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    handleDownload()
  }

  const handleMusicError = () => {
    console.error('Music load error for:', processedSrc)
    setLoadError('Failed to load music. The format may not be supported.')
    setMusicLoaded(false)
  }

  const handleMusicLoad = () => {
    setMusicLoaded(true)
    setLoadError(null)
  }

  const handlePlayPause = () => {
    const audio = document.querySelector(`audio[src="${finalSrc}"]`) as HTMLAudioElement
    if (!audio) return

    if (audio.paused) {
      audio.play()
      setIsPlaying(true)
    } else {
      audio.pause()
      setIsPlaying(false)
    }
  }

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = e.currentTarget
    setCurrentTime(audio.currentTime)
    setDuration(audio.duration || 0)
  }

  const formatTime = (time: number): string => {
    if (isNaN(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // For losi.one URLs, use direct HTTPS URLs since they now support it
  // For other URLs, use proxy for CORS handling
  const finalSrc = isLosiUrl
    ? processedSrc
    : `/api/music/download?url=${encodeURIComponent(processedSrc)}`

  return (
    <div className="relative my-4 w-full">
      {loadError ? (
        <div
          className={cn(
            'relative w-full overflow-hidden rounded-lg',
            !musicLoaded && !loadError && 'bg-muted/50'
          )}
        >
          <div className="w-full rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/10 dark:text-red-300">
            <p>{loadError}</p>
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              The music might still be processing or the format is not supported.
            </p>
          </div>
        </div>
      ) : (
        <AnimatePresence>
          {showMusic && (
            <BlurFade duration={0.6} delay={0.2} className="w-full">
              <div className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-4 dark:from-gray-800 dark:to-gray-900">
                {!musicLoaded && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center">
                    <ImageLoading />
                  </div>
                )}

                {/* Music Player UI */}
                <div className="flex items-center gap-4">
                  {/* Play/Pause Button */}
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-primary/10 hover:bg-primary/20 size-12 rounded-full p-0"
                    onClick={handlePlayPause}
                    disabled={!musicLoaded}
                  >
                    {isPlaying ? <IconPlayerPause size={20} /> : <IconPlayerPlay size={20} />}
                  </Button>

                  {/* Music Info */}
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-sm font-medium">
                      {alt || 'Generated Music'}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        {formatTime(currentTime)}
                      </span>
                      <div className="bg-muted h-1 flex-1 rounded-full">
                        <div
                          className="bg-primary h-full rounded-full transition-all duration-200"
                          style={{
                            width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'
                          }}
                        />
                      </div>
                      <span className="text-muted-foreground text-xs">{formatTime(duration)}</span>
                    </div>
                  </div>

                  {/* Download Button */}
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={isDownloading}
                    disabled={isDownloading}
                    className="text-foreground size-10 rounded-full bg-black/10 p-0 shadow-sm hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20"
                    onClick={handleButtonClick}
                  >
                    <IconDownload size={16} />
                  </Button>
                </div>

                {/* Hidden Audio Element */}
                <audio
                  src={finalSrc}
                  onLoadedData={handleMusicLoad}
                  onError={handleMusicError}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                  preload="metadata"
                  className="hidden"
                />
              </div>
            </BlurFade>
          )}
        </AnimatePresence>
      )}
    </div>
  )
}
